import React, { useEffect, useState } from 'react';
import { Col, Form, Row, Select, Table, Typography } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import PersianDatePicker from '../../components/PersianDatePicker';
import ReportPageLayout from './ReportPageLayout';
import ReportFiltersCard from './ReportFiltersCard';
import { reportsApi } from '../../utils/api';
import { getLast30DaysRange } from '../../utils/dateUtils';
import { extractRows, getDateRangePickerValue, parseDateRangeChange } from './reportUtils';
import { TableSkeleton } from '../../components/SkeletonLoaders';

const { Text } = Typography;

const AttendanceReport = () => {
  const range = getLast30DaysRange('jYYYY-MM-DD', 'jalali');
  const [filters, setFilters] = useState({ startDate: range.startDate, endDate: range.endDate, employee_id: 'all' });
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!filters.startDate || !filters.endDate) return;
    setLoading(true);
    try {
      const res = await reportsApi.getAttendanceReport(filters);
      setRows(extractRows(res));
      setEmployees(res?.filters?.employees || res?.employees || []);
      setMeta(res?.meta ?? res?.data?.meta ?? null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => load(), 350);
    return () => clearTimeout(timer);
  }, [filters]);

  return (
    <ReportPageLayout
      headerIcon={<CheckCircleOutlined />}
      title="گزارش حضور و غیاب"
      subtitle="خلاصه حضور، غیبت و ساعت کاری در بازه انتخابی"
    >
      <ReportFiltersCard>
        <Form layout="vertical" className="custom-font">
          <Row gutter={[16, 12]}>
            <Col xs={24} lg={12}>
              <Form.Item label="بازه تاریخ" className="mb-0">
                <PersianDatePicker
                  range
                  placeholder="انتخاب بازه از تاریخ تا تاریخ"
                  value={getDateRangePickerValue(filters.startDate, filters.endDate)}
                  onChange={(dates) => setFilters((p) => ({ ...p, ...parseDateRangeChange(dates) }))}
                  style={{ minHeight: 36 }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item label="کارمند" className="mb-0">
                <Select
                  value={filters.employee_id}
                  onChange={(v) => setFilters((p) => ({ ...p, employee_id: v }))}
                  options={[{ value: 'all', label: 'همه' }, ...employees.map((e) => ({ value: e.id, label: e.name }))]}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </ReportFiltersCard>

      {meta ? (
        <div className="flex flex-wrap gap-4 mb-4 text-sm custom-font rounded-xl border border-slate-100 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/30 px-4 py-3">
          <Text type="secondary">
            روزهای کاری: <strong className="text-slate-800 dark:text-slate-100">{meta?.total_working_days ?? '—'}</strong>
          </Text>
          <Text type="secondary">
            تعطیلات: <strong className="text-slate-800 dark:text-slate-100">{meta?.holidays_count ?? '—'}</strong>
          </Text>
        </div>
      ) : null}

      {loading ? (
        <TableSkeleton rows={6} columns={7} />
      ) : (
        <Table
          rowKey={(r, i) => r.id || i}
          dataSource={rows}
          scroll={{ x: 1000 }}
          className="custom-font"
          columns={[
            { title: 'کارمند', dataIndex: 'employee_name' },
            { title: 'روزهای حضور', dataIndex: 'present_days' },
            { title: 'روزهای اضافه', dataIndex: 'extra_days' },
            { title: 'روزهای غیبت', dataIndex: 'absent_days' },
            { title: 'ساعت کاری', dataIndex: 'hours_clocked' },
            { title: 'تعداد تاخیر', dataIndex: 'late_day_count' },
            { title: 'نیم‌روز', dataIndex: 'half_day_count' },
          ]}
        />
      )}
    </ReportPageLayout>
  );
};

export default AttendanceReport;
